/**
 * Production-Ready - ExistingDebtBuilder
 * Mirrors engine's existing-debt-builder.ts functionality
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import Decimal from 'decimal.js-light';
import { roundInterestToNearestCent } from '~/engine/processing/calculators/core/financial-math/rounding';
import { InterestCalculationEngine } from '~/engine/processing/engines/interest-engine/interest-calculator';
import type {
  DateRecord,
  ExistingDebtEntry,
  ExistingDebtInput,
  ExistingDebtPaymentRecord,
  ExistingDebtProject,
} from '~/types';
import { compareDates, getYear } from '~/utils/date-utils';

export class ExistingDebtBuilder {
  /**
   * Process a single existing debt project from input data
   */
  static processDebtProject(
    debtInput: ExistingDebtInput,
    availablePaymentDates?: DateRecord[]
  ): ExistingDebtProject {
    // Sort payment inputs by date to ensure correct order
    const sortedPayments = [...debtInput.existingDebtPaymentInputs].sort((a, b) =>
      compareDates(a.paymentDate, b.paymentDate)
    );

    // Convert inputs to payment records with calculations
    const paymentRecords: ExistingDebtPaymentRecord[] = [];

    // First pass: create base records
    sortedPayments.forEach((payment) => {
      paymentRecords.push({
        paymentDate: payment.paymentDate,
        interestRate: payment.interestRate || 0,
        principalPayment: payment.principalPayment || 0,
        principalInterest: payment.principalInterest || 0,
        totalPaymentInterest: 0, // Will be calculated
        totalFutureInterestObligation: 0, // Will be calculated
        totalPayment: 0, // Will be calculated
        fiscalYearInformation: {
          fiscalYearEndingDate: null, // Will be set if date mapping available
          isFinalPayment: false, // Will be calculated
          fiscalYearPaymentTotal: 0, // Will be calculated
        },
      });
    });

    // Second pass: calculate interest and totals (reverse order for cumulative calculations)
    for (let i = paymentRecords.length - 1; i >= 0; i--) {
      const record = paymentRecords[i];

      // Calculate principal interest for this payment using 30/360 day count
      let calculatedPrincipalInterest = 0;

      if (i > 0) {
        // Calculate interest from previous payment date to current payment date
        const previousRecord = paymentRecords[i - 1];
        calculatedPrincipalInterest = InterestCalculationEngine.calculateInterest(
          record.principalPayment,
          record.interestRate,
          previousRecord.paymentDate,
          record.paymentDate
        );
      } else {
        // First payment - assume standard semi-annual period
        // In production, we might want to use an actual issue date
        calculatedPrincipalInterest = InterestCalculationEngine.calculateSemiAnnualInterest(
          record.principalPayment,
          record.interestRate
        );
      }

      // Use provided interest or calculated interest
      // Wrap in Decimal to ensure precision in subsequent calculations
      const rawPrincipalInterest =
        record.principalInterest > 0 ? record.principalInterest : calculatedPrincipalInterest;

      // Get total interest from future payments (already rounded from previous iteration)
      const totalInterestFromFuture =
        i === paymentRecords.length - 1 ? 0 : paymentRecords[i + 1].totalPaymentInterest;

      // Calculate totals using Decimal for precision
      // Round ONCE at the final step to avoid accumulation errors
      const totalPaymentInterest = new Decimal(rawPrincipalInterest)
        .plus(totalInterestFromFuture)
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
        .toNumber();

      // Round principalInterest for storage (for consistency, but totalPaymentInterest is the authoritative value)
      const principalInterest = roundInterestToNearestCent(rawPrincipalInterest);

      const totalPayment = record.principalPayment + totalPaymentInterest;

      // Update the record
      paymentRecords[i] = {
        ...record,
        principalInterest,
        totalPaymentInterest,
        totalFutureInterestObligation: totalInterestFromFuture,
        totalPayment,
      };
    }

    // Third pass: calculate fiscal year information if date mapping available
    if (availablePaymentDates && availablePaymentDates.length > 0) {
      ExistingDebtBuilder.calculateFiscalYearInformation(paymentRecords, availablePaymentDates);
    }

    return {
      id: debtInput.id,
      name: debtInput.name,
      existingDebtPaymentRecords: paymentRecords,
    };
  }

  /**
   * Combine multiple debt projects into a single debt service schedule
   */
  static combineDebtProjects(debtProjects: ExistingDebtProject[]): ExistingDebtProject {
    if (debtProjects.length === 0) {
      return {
        id: 'combined',
        name: 'Combined Debt Service',
        existingDebtPaymentRecords: [],
      };
    }

    // Get all payment records from all projects
    const allRecords = debtProjects.flatMap((project) => project.existingDebtPaymentRecords);

    // Group records by payment date
    const recordsByDate = new Map<string, ExistingDebtPaymentRecord[]>();

    allRecords.forEach((record) => {
      const dateKey = record.paymentDate;
      if (!recordsByDate.has(dateKey)) {
        recordsByDate.set(dateKey, []);
      }
      recordsByDate.get(dateKey)!.push(record);
    });

    // Combine records for each date
    const combinedRecords: ExistingDebtPaymentRecord[] = [];

    for (const [paymentDate, records] of recordsByDate.entries()) {
      // Sum up the financial values
      // Round each sum to avoid floating point accumulation errors
      const principalPayment = records.reduce((sum, r) => sum + r.principalPayment, 0);
      const principalInterest = roundInterestToNearestCent(
        records.reduce((sum, r) => sum + r.principalInterest, 0)
      );
      const totalPaymentInterest = roundInterestToNearestCent(
        records.reduce((sum, r) => sum + r.totalPaymentInterest, 0)
      );
      const totalPayment = principalPayment + totalPaymentInterest;

      // Use fiscal year info from the first record (they should all be the same for the same date)
      const fiscalYearInformation = records[0].fiscalYearInformation;

      combinedRecords.push({
        paymentDate,
        interestRate: 0, // Combined rate doesn't have meaningful value
        principalPayment,
        principalInterest,
        totalPaymentInterest,
        totalFutureInterestObligation: 0, // Will be recalculated
        totalPayment,
        fiscalYearInformation,
      });
    }

    // Sort combined records by date
    combinedRecords.sort((a, b) => compareDates(a.paymentDate, b.paymentDate));

    return {
      id: 'combined',
      name: 'Combined Debt Service',
      existingDebtPaymentRecords: combinedRecords,
    };
  }

  /**
   * Convert processed debt projects to table data format
   */
  static convertToTableData(debtProjects: ExistingDebtProject[]): ExistingDebtEntry[] {
    return debtProjects.flatMap((project) =>
      project.existingDebtPaymentRecords.map((record, index) => ({
        id: `${project.id}-${index}`,
        name: project.name,
        paymentDate: record.paymentDate,
        principal: record.principalPayment,
        interest: record.principalInterest,
        totalPayment: record.totalPayment,
        interestRate: record.interestRate,
        totalPaymentInterest: record.totalPaymentInterest,
        isFinalPayment: record.fiscalYearInformation.isFinalPayment,
        fiscalYear: ExistingDebtBuilder.extractFiscalYear(
          record.paymentDate,
          record.fiscalYearInformation
        ),
      }))
    );
  }

  /**
   * Validate existing debt input
   */
  static validateDebtInput(debtInput: ExistingDebtInput): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!debtInput.id.trim()) {
      errors.push('Debt ID is required');
    }

    if (!debtInput.name.trim()) {
      errors.push('Debt name is required');
    }

    if (debtInput.existingDebtPaymentInputs.length === 0) {
      errors.push('At least one payment entry is required');
    }

    // Validate payment entries
    debtInput.existingDebtPaymentInputs.forEach((payment, index) => {
      if (!payment.paymentDate) {
        errors.push(`Payment ${index + 1}: Payment date is required`);
      }

      if (payment.principalPayment < 0) {
        errors.push(`Payment ${index + 1}: Principal payment cannot be negative`);
      }

      if (payment.principalInterest < 0) {
        errors.push(`Payment ${index + 1}: Interest payment cannot be negative`);
      }

      if (payment.interestRate && (payment.interestRate < 0 || payment.interestRate > 1)) {
        errors.push(`Payment ${index + 1}: Interest rate must be between 0 and 1 (0% to 100%)`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Build summary statistics for debt projects
   */
  static buildSummary(debtProjects: ExistingDebtProject[]) {
    if (debtProjects.length === 0) return null;

    const allRecords = debtProjects.flatMap((project) => project.existingDebtPaymentRecords);

    const totalPrincipal = allRecords.reduce((sum, record) => sum + record.principalPayment, 0);
    const totalInterest = allRecords.reduce((sum, record) => sum + record.principalInterest, 0);
    const totalPayments = allRecords.reduce((sum, record) => sum + record.totalPayment, 0);

    const paymentDates = allRecords.map((r) => r.paymentDate).sort((a, b) => compareDates(a, b));
    const startDate = paymentDates[0];
    const endDate = paymentDates[paymentDates.length - 1];

    return {
      projectCount: debtProjects.length,
      totalPrincipal,
      totalInterest,
      totalPayments,
      paymentCount: allRecords.length,
      startDate: startDate,
      endDate: endDate,
      averagePayment: totalPayments / allRecords.length,
    };
  }

  /**
   * Convert detailed debt project to simplified format for external use
   */
  static convertToSimplifiedFormat(project: ExistingDebtProject): ExistingDebtProject {
    const payments = project.existingDebtPaymentRecords.map((record) => ({
      paymentDate: record.paymentDate,
      principal: record.principalPayment,
      interest: record.principalInterest,
      totalPayment: record.totalPayment,
      fiscalYear: ExistingDebtBuilder.extractFiscalYear(
        record.paymentDate,
        record.fiscalYearInformation
      ),
    }));

    const totalPrincipal = payments.reduce((sum, p) => sum + p.principal, 0);
    const totalInterest = payments.reduce((sum, p) => sum + p.interest, 0);
    const totalPayments = payments.reduce((sum, p) => sum + p.totalPayment, 0);

    return {
      ...project,
      payments,
      totalPrincipal,
      totalInterest,
      totalPayments,
    };
  }

  /**
   * Convert multiple detailed debt projects to simplified format
   */
  static convertAllToSimplifiedFormat(projects: ExistingDebtProject[]): ExistingDebtProject[] {
    return projects.map((project) => ExistingDebtBuilder.convertToSimplifiedFormat(project));
  }

  private static calculateFiscalYearInformation(
    paymentRecords: ExistingDebtPaymentRecord[],
    availablePaymentDates: DateRecord[]
  ) {
    // Map each payment to its fiscal year
    paymentRecords.forEach((record) => {
      const paymentDate = record.paymentDate;
      const matchingDateRecord = ExistingDebtBuilder.findMatchingDateRecord(
        paymentDate,
        availablePaymentDates
      );

      if (matchingDateRecord) {
        record.fiscalYearInformation.fiscalYearEndingDate = matchingDateRecord.fiscalYearEndDate;

        // Check if this is the last payment in the fiscal year
        const isLastInFiscalYear = ExistingDebtBuilder.isLastPaymentInFiscalYear(
          record.paymentDate,
          paymentRecords,
          matchingDateRecord.fiscalYearEndDate
        );

        record.fiscalYearInformation.isFinalPayment = isLastInFiscalYear;
      }
    });

    // Calculate fiscal year payment totals
    const fiscalYearTotals = new Map<string, number>();

    paymentRecords.forEach((record) => {
      const fiscalYearEnd = record.fiscalYearInformation.fiscalYearEndingDate;
      if (fiscalYearEnd) {
        const current = fiscalYearTotals.get(fiscalYearEnd) || 0;
        fiscalYearTotals.set(fiscalYearEnd, current + record.totalPayment);
      }
    });

    // Update records with fiscal year totals
    paymentRecords.forEach((record) => {
      const fiscalYearEnd = record.fiscalYearInformation.fiscalYearEndingDate;
      if (fiscalYearEnd) {
        record.fiscalYearInformation.fiscalYearPaymentTotal =
          fiscalYearTotals.get(fiscalYearEnd) || 0;
      }
    });
  }

  private static findMatchingDateRecord(
    paymentDate: string,
    dateRecords: DateRecord[]
  ): DateRecord | null {
    return (
      dateRecords.find((record) => record.paymentDates.some((date) => date === paymentDate)) || null
    );
  }

  private static isLastPaymentInFiscalYear(
    paymentDate: string,
    allRecords: ExistingDebtPaymentRecord[],
    fiscalYearEnd: string
  ): boolean {
    const fiscalYearRecords = allRecords.filter(
      (r) => r.fiscalYearInformation.fiscalYearEndingDate === fiscalYearEnd
    );

    const sortedDates = fiscalYearRecords
      .map((r) => r.paymentDate)
      .sort((a, b) => compareDates(a, b));

    return sortedDates[sortedDates.length - 1] === paymentDate;
  }

  private static extractFiscalYear(
    paymentDate: string,
    fiscalYearInfo: ExistingDebtPaymentRecord['fiscalYearInformation']
  ): number {
    if (fiscalYearInfo.fiscalYearEndingDate) {
      return getYear(fiscalYearInfo.fiscalYearEndingDate);
    }

    // Fallback to payment date year
    return getYear(paymentDate);
  }
}
